import { useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, Badge, Avatar } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { apiClientPaginated } from '@/lib/api-client';

// ─── Design tokens ────────────────────────��─────────────────────────���───────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login_at?: string;
}

const ROLE_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
  OWNER: 'success',
  MANAGER: 'info',
  CASHIER: 'warning',
};

const columns: ColumnDef<UserRow>[] = [
  {
    key: 'name',
    header: 'User',
    render: (v, row) => (
      <div className="flex items-center gap-2">
        <Avatar name={v as string} size="sm" />
        <div>
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{v as string}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{row.email}</p>
        </div>
      </div>
    ),
  },
  {
    key: 'role',
    header: 'Role',
    render: (v) => (
      <Badge variant={ROLE_VARIANT[v as string] ?? 'info'}>
        {v as string}
      </Badge>
    ),
  },
  {
    key: 'is_active',
    header: 'Status',
    render: (v) => (
      <Badge variant={(v as boolean) ? 'success' : 'danger'}>
        {(v as boolean) ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    key: 'last_login_at',
    header: 'Last Active',
    render: (v) => (
      <span className="tabular-nums text-xs text-neutral-500 dark:text-neutral-400">
        {(v as string)
          ? new Date(v as string).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : '—'}
      </span>
    ),
  },
];

interface TenantUsersTabProps {
  tenantId: string;
}

export function TenantUsersTab({ tenantId }: TenantUsersTabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['tenant', tenantId, 'users'],
    queryFn: () => apiClientPaginated<UserRow[]>(`/users?tenant_id=${tenantId}&limit=50`),
  });

  const users = data?.data ?? [];

  if (!isLoading && users.length === 0) {
    return (
      <div className="animate-slide-up">
        <EmptyState
          variant="page"
          title="No users besides owner"
          description="Invite team members to start managing this business"
        />
      </div>
    );
  }

  return (
    <div className={`${GLASS_PANEL} overflow-hidden animate-slide-up transition-shadow duration-200 hover:shadow-lg`}>
      <DataTable
        columns={columns}
        data={users}
        loading={isLoading}
        variant="default"
        emptyState={{ title: 'No users', description: 'No users found for this tenant' }}
      />
    </div>
  );
}
